<script setup>
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import SectionTitle from '@weathernews/btos-vuecomponent/with-onebox-common-elements/src/components/SectionTitle.vue'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)

const props = defineProps({
  calendarData: {
    type: Object,
    required: true
  },
  year: {
    type: String,
    required: true
  },
  month: {
    type: String,
    required: true
  },
  title: {
    type: String,
    default: '過去の天気（天気図）'
  }
})

const route = useRoute()
const config = useRuntimeConfig()
const yearList = generateYearList()
const selectedYear = ref(String(props.year))
const selectedMonth = ref(Number(props.month))
const selectedContent = ref(route.path.split('/').find(Boolean))
const monthScrollRef = ref(null)
const monthScrollLeft = useCookie('pastMonthScroll', () => 0)
const imageStatusMap = ref({})
const isDesktop = useMediaQuery('(min-width: 830px)')

const filteredCalendar = computed(() => {
  if (!isDesktop.value) {
    return props.calendarData.calendar.filter((item) => item.isMobile !== false)
  }
  return props.calendarData.calendar
})

const maxMonth = computed(() => {
  const selectedYearData = yearList.find((item) => item.year === selectedYear.value)
  return selectedYearData ? selectedYearData.maxMonth : 12
})

// ルートの先頭セグメントを除いた残りのパスを取得
const trailingPath = computed(() => {
  const parts = route.path.split('/').filter(Boolean)
  if (parts.length <= 1) return ''
  return `${parts.slice(1).join('/')}/`
})

watch([selectedYear, selectedMonth], () => {
  if (selectedYear.value === yearList.at(-1).year && selectedMonth.value > yearList.at(-1).maxMonth) {
    selectedMonth.value = yearList.at(-1).maxMonth
  }
  return navigateTo(`/${selectedContent.value}/${selectedYear.value}/${String(selectedMonth.value).padStart(2, '0')}/`)
})

watch(selectedContent, () => {
  return navigateTo(`/${selectedContent.value}/${trailingPath.value}`)
})

const getImageKey = (item) => String(item.tm)
const handleImgLoad = (key) => {
  imageStatusMap.value[key] = 'loaded'
}
const handleImgError = (key) => {
  imageStatusMap.value[key] = 'error'
}

onMounted(() => {
  filteredCalendar.value.forEach((item) => {
    const key = getImageKey(item)
    if (!(key in imageStatusMap.value)) {
      imageStatusMap.value[key] = 'pending'
    }
  })
  if (monthScrollRef.value) {
    monthScrollRef.value.scrollLeft = monthScrollLeft.value
    const selectedButton = monthScrollRef.value.querySelector('[data-selected="true"]')
    if (selectedButton) {
      selectedButton.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      })
    }
    monthScrollRef.value.addEventListener('scroll', () => {
      monthScrollLeft.value = monthScrollRef.value.scrollLeft
    })
  }
})

</script>

<template>
  <div class="pastContent">
    <SectionTitle
      :title="title"
      :date="`${dayjs.unix(calendarData.announce).tz('Asia/Tokyo').format('HH:mm')}更新`"
    />
    <div class="date">
      <div class="year">
        <select
          id="year"
          v-model="selectedYear"
          name="year"
        >
          <option
            v-for="(item, index) in yearList"
            :key="index"
            :value="item.year"
          >
            {{ item.year }}年
          </option>
        </select>
      </div>
      <div
        ref="monthScrollRef"
        class="month"
      >
        <button
          v-for="n in 12"
          :key="n"
          :disabled="n > maxMonth"
          :data-selected="n === Number(selectedMonth)"
          @click="selectedMonth = n"
        >
          {{ n }}月
        </button>
      </div>
    </div>
    <div class="type">
      <div
        v-for="(item, index) in contentList"
        :key="index"
        class="button"
      >
        <button
          :data-selected="item.path === selectedContent"
          @click="selectedContent = item.path"
        >
          {{ item.text }}
        </button>
      </div>
    </div>
    <div class="contents">
      <NuxtLink
        v-for="(item, index) in filteredCalendar"
        :key="index"
        :data-disabled="!item.pc || !item.sp"
        :data-highlight="item.isMobile"
        :to="`/${selectedContent}/${dayjs.unix(item.tm).format('YYYY/MM/DD')}/`"
      >
        <p
          :class="{
            holiday: item.isHoliday || dayjs.unix(item.tm).day() === 0,
            saturday: !item.isHoliday && dayjs.unix(item.tm).day() === 6
          }"
        >
          {{ dayjs.unix(item.tm).format('D日(ddd)') }}
        </p>
        <figure>
          <ClientOnly>
            <template v-if="item.pc && item.sp">
              <picture v-if="imageStatusMap[getImageKey(item)] !== 'error'">
                <source
                  :srcset="`${config.public.imgDomain}/thumbnail/${item.pc}/small.webp`"
                  media="(min-width: 830px)"
                >
                <img
                  :src="`${config.public.imgDomain}/thumbnail/${item.sp}/medium.webp`"
                  :alt="`${dayjs.unix(item.tm).format('YYYY年M月D日')}の${contentsNames[selectedContent]}`"
                  :style="{ opacity: imageStatusMap[getImageKey(item)] === 'loaded' ? 1 : 0 }"
                  :fetchpriority="index < 15 ? 'high' : 'auto'"
                  @load="handleImgLoad(getImageKey(item))"
                  @error="handleImgError(getImageKey(item))"
                >
              </picture>
              <p v-else>
                no data
              </p>
            </template>
          </ClientOnly>
        </figure>
      </NuxtLink>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pastContent {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .date {
    display: flex;
    align-items: center;
    gap: 8px;
    .year {
      position: relative;
      border-right: 1px solid #C6CCDC;
      &::before {
        position: absolute;
        top: 50%;
        right: 10px;
        transform: translateY(-50%);
        width: 8px;
        height: 5px;
        background-color: #3569C0;
        clip-path: polygon(0 0, 100% 0, 50% 100%);
        content: '';
      }
      select {
        width: 100%;
        padding-right: 23px;
        appearance: none;
        outline: none;
        cursor: pointer;
      }
      option {
        background-color: #000;
      }
    }
    .month {
      display: flex;
      align-items: center;
      gap: 5px;
      flex: 1;
      overflow-x: auto;
      padding-bottom: 4px;
      &::-webkit-scrollbar {
        width: 4px;
        height: 4px;
      }
      &::-webkit-scrollbar-track {
        border-radius: 100vmax;
        background-color: transparent;
      }
      &::-webkit-scrollbar-thumb {
        border-radius: 100vmax;
        background-color: #808080;
      }
      button {
        padding: 4px 10px;
        border-radius: 4px;
        border: 1px solid #F0F3F9;
        background-color: #F9F9F9;
        color: #000;
        text-align: center;
        font-size: 13px;
        font-weight: 300;
        line-height: 1em;
        white-space: nowrap;
        user-select: none;
        cursor: pointer;
        &[data-selected="true"] {
          background-color: #3569C0;
          border-color: #3569C0;
          color: #FFF;
          pointer-events: none;
        }
        &:disabled {
          opacity: 0.4;
          pointer-events: none;
        }
      }
    }
  }
  .type {
    display: flex;
    align-items: center;
    padding-bottom: 8px;
    overflow-x: auto;
    &::-webkit-scrollbar {
      display: none;
    }
    .button {
      flex: 1;
      background-color: #EEF1F8;
      &:first-child {
        border-top-left-radius: 100vmax;
        border-bottom-left-radius: 100vmax;
      }
      &:last-child {
        border-top-right-radius: 100vmax;
        border-bottom-right-radius: 100vmax;
      }
    }
    button {
      width: 100%;
      padding: 7px;
      border-radius: 100vmax;
      min-width: 110px;
      color: #303030;
      text-align: center;
      font-size: 13px;
      font-weight: 300;
      line-height: 1em;
      white-space: nowrap;
      user-select: none;
      cursor: pointer;
      &[data-selected="true"] {
        position: relative;
        background-color: #3569C0;
        color: #FFF;

        &::before {
          position: absolute;
          left: 50%;
          bottom: -8px;
          transform: translateX(-50%);
          width: 16px;
          height: 11px;
          background-color: #3569C0;
          clip-path: polygon(0 0, 100% 0, 50% 100%);
          content: '';
        }
      }
    }
  }
  .contents {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 5px;
    a {
      display: none;
      flex-direction: column;
      &[data-disabled="true"] {
        opacity: 0.6;
        pointer-events: none;
      }
      &[data-highlight="true"] {
        display: flex;
      }
      > p {
        padding-block: 5px;
        color: #303030;
        text-align: center;
        font-size: 13px;
        font-weight: 300;
        line-height: 1em;
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
        border: 1px solid #F0F3F9;
        background-color: #F9F9F9;
        &.holiday {
          color: #FF3000;
        }
        &.saturday {
          color: #0060FF;
        }
      }
      figure {
        width: 100%;
        height: auto;
        aspect-ratio: 75 / 68;
        background-color: #F9F9F9;
        img {
          width: 100%;
          height: 100%;
        }
        > p{
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          color: #808080;
          font-size: 16px;
          font-weight: 300;
          line-height: 1em;
        }
      }
      @media (any-hover: hover) {
        transition: opacity .2s ease-in-out;
        cursor: pointer;
        &:hover {
          opacity: .6;
        }
      }
    }
  }
}
@media (min-width: 830px) {
  .pastContent {
    .date {
      .month {
        padding-bottom: 2px;
        button {
          flex: 1;
        }
      }
    }
    .type {
      .button {
        flex: 1;
      }
      button {
        width: 100%;
      }
    }
    .contents {
      grid-template-columns: repeat(7, 1fr);
      padding: 5px;
      a {
        display: flex;
        &[data-highlight="true"] {
          outline: 5px solid #C2D2EC;
          > p {
            border-left: none;
            border-right: none;
            border-top-left-radius: 0px;
            border-top-right-radius: 0px;
          }
        }
        figure {
          aspect-ratio: 151 / 100;
          > p {
            font-size: 12px;
            line-height: 1em;
          }
        }
      }
    }
  }
}
</style>
